import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { SuppliersOnboardingOverlay } from '@features/tutorial';
import { useSuppliers } from '../../hooks/useSuppliers';
import { SuppliersTable } from './SuppliersTable';
import CreateSupplierBottomSheet from '../create/CreateSupplierBottomSheet';
import EmptyState from '@shared/ui/common/EmptyState';
import { Paper } from '@shared/ui/common/paper';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';

const Suppliers = () => {
  const { suppliers, isLoading, error } = useSuppliers();
  const [search, setSearch] = useState('');

  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const filteredSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(q));
  }, [suppliers, search]);

  const handleCreateSupplier = () => {
    openBottomSheet({
      title: 'Create Supplier',
      component: <CreateSupplierBottomSheet handleClose={async () => { await closeBottomSheet(); }} />,
    });
  };

  return (
    <PageLayout title="Suppliers">
      <SuppliersOnboardingOverlay />
      <Stack
        direction="row"
        minHeight={48}
        py={2}
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 99,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          minWidth: 0,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 0,
            maxWidth: 400,
            flex: '1 1 240px',
            '& .MuiOutlinedInput-root': { borderRadius: 1 },
          }}
        />
        <Button
          variant="text"
          color="secondary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleCreateSupplier}
          sx={{ flexShrink: 0 }}
        >
          Create Supplier
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <Typography color="error">Error loading suppliers: {(error as Error).message}</Typography>
        </Box>
      ) : suppliers.length === 0 ? (

        <EmptyState
          title="No suppliers found"
          description="Create your first supplier to get started"
          action={{
            label: 'Create Supplier',
            onClick: handleCreateSupplier,
          }}
        />

      ) : filteredSuppliers.length === 0 ? (
        <EmptyState
          title="No suppliers found"
          description={`No suppliers match &quot;${search.trim()}&quot;. Try a different name.`}
        />

      ) : (

        <SuppliersTable suppliers={filteredSuppliers} />

      )}
    </PageLayout>
  );
};

export default Suppliers;
